'use client'

import { useIntl } from 'react-intl'
import { Dialog, Cell, Text, Button, Divider, Navigation, BottomSheet } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  EstimatedRefundFactualShippingMethod,
  RefundEstimateModel,
  ReturnShippingOption,
  ReturnShippingOptionModel,
} from '@marketplace-web/complaints/complaints-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

type Props = {
  refundEstimate?: RefundEstimateModel | null
  isSellerBusinessUser?: boolean
  isOpen: boolean
  selectedShippingOption?: ReturnShippingOption
  integratedReturnShippingOption?: ReturnShippingOptionModel
  isOpenedInDialog?: boolean
  onClose: () => void
}

const ComplaintCurrencyConversionModal = ({
  isOpen,
  onClose,
  refundEstimate,
  isSellerBusinessUser = false,
  selectedShippingOption,
  integratedReturnShippingOption,
  isOpenedInDialog = false,
}: Props) => {
  const { formatNumber, locale } = useIntl()
  const translate = useTranslate('complaint_currency_conversion_dialog')
  const buyerProtectionFeeTranslationKey = isSellerBusinessUser
    ? 'buyer_protection_pro_fee_refund'
    : 'buyer_protection_fee_refund'

  const { phones } = useBreakpoint()
  const isMobileView = phones && !isOpenedInDialog

  const isFactualShippingIntegrated =
    refundEstimate?.beforeConversion?.details.factualShippingMethod ===
    EstimatedRefundFactualShippingMethod.Integrated
  const isReturnShippingIntegrated = selectedShippingOption === ReturnShippingOption.Integrated

  const renderNavigation = () => {
    return (
      <Navigation
        body={translate('title')}
        right={
          <Button
            inline
            iconName={X24}
            styling={Button.Styling.Flat}
            onClick={onClose}
            aria={{ 'aria-label': translate('a11y.close') }}
          />
        }
      />
    )
  }

  const renderItemPriceConversionInfo = () => {
    if (!refundEstimate?.beforeConversion) return null

    const priceBeforeConversion = formatCurrencyAmount(
      refundEstimate.beforeConversion.details.totalItemPrice,
      locale,
    )
    const priceAfterConversion = formatCurrencyAmount(
      refundEstimate.details.itemsEstimatedRefundTotal,
      locale,
    )

    return (
      <Cell
        title={translate('items')}
        body={
          <Text
            as="h3"
            type={Text.Type.Subtitle}
            html
            text={translate('items_price_description', { cost: priceBeforeConversion })}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  const renderShippingPriceConversionInfo = () => {
    if (!refundEstimate?.beforeConversion) return null

    const priceBeforeConversion = formatCurrencyAmount(
      refundEstimate.beforeConversion.details.shipment,
      locale,
    )
    const priceAfterConversion = formatCurrencyAmount(refundEstimate.details.shipment, locale)

    if (parseFloat(refundEstimate.beforeConversion.details.shipment.amount) === 0) {
      return (
        <Cell
          title={translate('shipping_refund')}
          body={
            <Text
              as="h3"
              type={Text.Type.Subtitle}
              text={
                isFactualShippingIntegrated
                  ? translate('shipping_refund_description.covered_by_vinted')
                  : translate('shipping_refund_description.covered_by_seller')
              }
            />
          }
          suffix={priceAfterConversion}
        />
      )
    }

    return (
      <Cell
        title={translate('shipping_refund')}
        body={
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('shipping_refund_description.converted', {
              cost: priceBeforeConversion,
            })}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  const renderBuyerProtectionFeeInfo = () => {
    if (!refundEstimate?.beforeConversion) return null

    const price = formatCurrencyAmount(refundEstimate.details.buyerProtectionFee, locale)

    return <Cell title={translate(buyerProtectionFeeTranslationKey)} suffix={price} />
  }

  const renderAuthenticityCheckFee = () => {
    if (!refundEstimate?.details.authenticityCheckFee) return null

    const price = formatCurrencyAmount(refundEstimate.details.authenticityCheckFee, locale)

    return <Cell title={translate('authenticity_check_fee_refund')} suffix={price} />
  }

  const renderElectronicsVerificationFee = () => {
    if (!refundEstimate?.details.electronicsVerificationFee) return null

    const price = formatCurrencyAmount(refundEstimate.details.electronicsVerificationFee, locale)

    return <Cell title={translate('electronics_check_fee_refund')} suffix={price} />
  }

  const renderItemDiscountsInfo = () => {
    if (!refundEstimate?.details.totalItemsDiscount) return null

    const price = formatCurrencyAmount(refundEstimate.details.totalItemsDiscount, locale, {
      formatAsNegative: true,
    })

    return <Cell title={translate('item_discounts')} suffix={price} />
  }

  const renderExchangeRate = () => {
    if (!refundEstimate?.beforeConversion) return null

    const { baseRate, exchangeRate } = refundEstimate.beforeConversion.exchange

    const base = formatCurrencyAmount(baseRate, locale)
    const rate = formatCurrencyAmount(exchangeRate, locale)

    return (
      <>
        <Divider />
        <Cell>
          <Text
            as="span"
            text={`${translate('fx_rate.title')} ${translate('fx_rate.rate', { rate, base })}`}
          />
        </Cell>
      </>
    )
  }

  const renderPaymentServiceProviderFee = () => {
    if (!refundEstimate?.beforeConversion) return null

    const { rate, provider } = refundEstimate.beforeConversion.markup

    const feePercentage = formatNumber(rate, {
      style: 'percent',
      maximumFractionDigits: 2,
    })

    return (
      <Cell
        body={
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('fx_markup_rate', { fee: feePercentage, payment_provider: provider })}
          />
        }
      />
    )
  }

  const renderReturnShippingPrice = () => {
    if (!refundEstimate?.details.shipment || !refundEstimate.beforeConversion) return null
    if (!selectedShippingOption) return null
    if (!isReturnShippingIntegrated) return null
    if (integratedReturnShippingOption?.sellerPaidForReturn) return null

    if (parseFloat(refundEstimate.details.shipment.amount) === 0 && isFactualShippingIntegrated)
      return (
        <Cell
          title={translate('return_shipping')}
          body={
            <Text
              as="h3"
              type={Text.Type.Subtitle}
              text={translate('shipping_refund_description.covered_by_vinted')}
            />
          }
          suffix={formatCurrencyAmount(refundEstimate.details.shipment, locale)}
        />
      )

    const priceBeforeConversion = formatCurrencyAmount(
      refundEstimate.beforeConversion.details.shipment,
      locale,
    )
    const priceAfterConversion = formatCurrencyAmount(refundEstimate.details.shipment, locale, {
      formatAsNegative: true,
    })

    return (
      <Cell
        title={translate('return_shipping')}
        body={
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('return_shipping_description', {
              cost: priceBeforeConversion,
            })}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  const renderActions = () => {
    return (
      <Cell>
        <Button text={translate('actions.ok')} styling={Button.Styling.Filled} onClick={onClose} />
      </Cell>
    )
  }

  const renderContent = () => {
    return (
      <>
        <SeparatedList separator={<Divider />}>
          {renderNavigation()}
          {renderItemPriceConversionInfo()}
          {renderItemDiscountsInfo()}
          {renderBuyerProtectionFeeInfo()}
          {renderAuthenticityCheckFee()}
          {renderElectronicsVerificationFee()}
          {renderShippingPriceConversionInfo()}
          {renderReturnShippingPrice()}
        </SeparatedList>
        {renderExchangeRate()}
        {renderPaymentServiceProviderFee()}
      </>
    )
  }

  if (isMobileView) {
    return (
      <BottomSheet isVisible={isOpen} onClose={onClose}>
        {renderContent()}
      </BottomSheet>
    )
  }

  return (
    <Dialog show={isOpen} defaultCallback={onClose} closeOnOverlay>
      {renderContent()}
      {renderActions()}
    </Dialog>
  )
}

export default ComplaintCurrencyConversionModal
